import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassCardTitle,
} from "@/components/ui/glass-card";
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Tooltip as UITooltip,
} from "@/components/ui/tooltip";
import type { OptimizationParameters } from "@/types/api";
import { cn } from "@/utils/cn";
import {
  getParameterDescription,
  getParameterDisplayName,
} from "@/utils/parameterNames";
import { generateTomlConfig } from "@/utils/tomlGenerator";
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  FileJson,
  FileCode,
  Pin,
  Settings,
  Target,
  TrendingUp,
  Zap,
} from "lucide-react";
import { useState, useMemo } from "react";
import * as toml from "@iarna/toml";

interface StudyConfigurationPanelProps {
  studyName: string;
  symbol?: string;
  timeframe?: string;
  parameters?: OptimizationParameters | null;
  createdAt?: string | null;
  completedAt?: string | null;
  nTrials?: number | null;
  tomlConfig?: string | null;
}

interface StudyMetadata {
  parameters: OptimizationParameters | null;
  created_at: string | null;
  completed_at: string | null;
  n_trials: number | null;
  objective_function?: string;
  min_trades_required?: number;
  toml_config?: string | null;
}

type FormatType = "toml" | "json";

export function StudyConfigurationPanel({
  studyName,
  symbol,
  timeframe,
  parameters,
  createdAt,
  completedAt,
  nTrials,
  tomlConfig,
}: StudyConfigurationPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const studyMetadata: StudyMetadata = {
    parameters: parameters ?? null,
    created_at: createdAt ?? null,
    completed_at: completedAt ?? null,
    n_trials: nTrials ?? null,
    toml_config: tomlConfig ?? null,
  };

  // Parse TOML config to extract sampler seed and other study configuration
  const parseSamplerSeed = (tomlConfig: string | null | undefined): number | null => {
    if (!tomlConfig) return null;

    // Simple regex to extract seed from [study.sampler] section
    const seedMatch = tomlConfig.match(/\[study\.sampler\][^\[]*seed\s*=\s*(\d+)/);
    if (seedMatch && seedMatch[1]) {
      return parseInt(seedMatch[1], 10);
    }

    return null;
  };

  const parseMinTradesRequired = (tomlConfig: string | null | undefined): number | null => {
    if (!tomlConfig) return null;

    // Extract min_trades_required from [fixed_parameters] section
    const match = tomlConfig.match(/\[fixed_parameters\][^\[]*min_trades_required\s*=\s*(\d+)/);
    if (match && match[1]) {
      return parseInt(match[1], 10);
    }

    return null;
  };

  /**
   * Parse TOML config and merge fixed_parameters into the parameters object.
   * This fixes the bug where pinned filters don't show in the Study Configuration panel.
   *
   * Enhanced to also detect optimized (tested) filters from the [parameters] section.
   *
   * Flow:
   * 1. If TOML config exists, parse it
   * 2. Extract both `fixed_parameters` and `parameters` sections
   * 3. Merge fixed_parameters (pinned values) with parameters
   * 4. Detect optimized filters from [parameters] section (categorical with [true, false] choices)
   * 5. Return complete parameter object with isPinned and isOptimized metadata
   */
  const parseAndMergeParameters = (
    baseParameters: OptimizationParameters | null,
    tomlConfig: string | null | undefined
  ): { params: OptimizationParameters; pinnedParams: Set<string>; optimizedParams: Set<string> } => {
    const pinnedParams = new Set<string>();
    const optimizedParams = new Set<string>();

    // Start with base parameters from database (may be incomplete)
    let mergedParams: any = baseParameters ? { ...baseParameters } : {};

    if (tomlConfig) {
      try {
        // Parse TOML configuration
        const parsedToml = toml.parse(tomlConfig) as any;

        // Extract fixed_parameters section (contains pinned boolean filters)
        const fixedParams = parsedToml.fixed_parameters || {};

        // Extract parameters section (contains optimized/tested parameters)
        const parameters = parsedToml.parameters || {};

        // Boolean filter keys to check
        const booleanFilterKeys = [
          'usedynamicexits',
          'useemafilter',
          'usesmafilter',
          'usevolatilityfilter',
          'useregimefilter',
          'useadxfilter',
        ];

        // Check each boolean filter
        booleanFilterKeys.forEach((key) => {
          // Check if pinned in fixed_parameters
          if (key in fixedParams) {
            mergedParams[key] = fixedParams[key];
            pinnedParams.add(key);
          }
          // Check if optimized in parameters section
          else if (
            key in parameters &&
            parameters[key]?.type === 'categorical' &&
            Array.isArray(parameters[key]?.choices) &&
            parameters[key].choices.length === 2
          ) {
            optimizedParams.add(key);
            // Get value from baseParameters (contains best trial result)
            if (baseParameters && key in baseParameters) {
              mergedParams[key] = baseParameters[key as keyof OptimizationParameters];
            }
          }
        });

        // Also handle other fixed parameters that might be pinned
        Object.keys(fixedParams).forEach((key) => {
          if (!booleanFilterKeys.includes(key)) {
            // For non-boolean parameters, they might be in fixed_parameters too
            mergedParams[key] = fixedParams[key];
            pinnedParams.add(key);
          }
        });

      } catch (error) {
        console.error('Failed to parse TOML config:', error);
        // Fall back to base parameters if parsing fails
      }
    }

    return {
      params: mergedParams as OptimizationParameters,
      pinnedParams,
      optimizedParams,
    };
  };

  // Use useMemo to merge parameters only when they change
  const { params, pinnedParams, optimizedParams } = useMemo(() => {
    return parseAndMergeParameters(studyMetadata?.parameters || null, studyMetadata?.toml_config);
  }, [studyMetadata?.parameters, studyMetadata?.toml_config]);

  const generateConfigContent = (format: FormatType): string => {
    if (!studyMetadata?.parameters) return "";

    if (format === "toml") {
      // Extract sampler seed from stored TOML config
      const samplerSeed = parseSamplerSeed(studyMetadata.toml_config);

      // Convert parameters to the format expected by tomlGenerator
      const config = {
        n_trials: studyMetadata.n_trials ?? 100,
        neighborscount_range: studyMetadata.parameters.neighborscount_range,
        maxbarsback_range: studyMetadata.parameters.maxbarsback_range,
        emaperiod: studyMetadata.parameters.emaperiod,
        smaperiod: studyMetadata.parameters.smaperiod,
        lookbackwindow: studyMetadata.parameters.lookbackwindow,
        relativeweight: studyMetadata.parameters.relativeweight,
        regressionlevel: studyMetadata.parameters.regressionlevel,
        crossoverlag: studyMetadata.parameters.crossoverlag,
        regimethreshold: studyMetadata.parameters.regimethreshold,
        adxthreshold: studyMetadata.parameters.adxthreshold,
        usevolatilityfilter: studyMetadata.parameters.usevolatilityfilter,
        useregimefilter: studyMetadata.parameters.useregimefilter,
        useadxfilter: studyMetadata.parameters.useadxfilter,
        usesmafilter: studyMetadata.parameters.usesmafilter,
        useemafilter: studyMetadata.parameters.useemafilter,
        usedynamicexits: studyMetadata.parameters.usedynamicexits,
      };

      return generateTomlConfig({
        config,
        studyName,
        symbol: symbol || "BTC_USD",
        timeframe: timeframe || "D1",
        samplerSeed,
      });
    } else {
      // JSON format
      const samplerSeed = parseSamplerSeed(studyMetadata.toml_config);
      const minTradesRequired = parseMinTradesRequired(studyMetadata.toml_config);

      const config = {
        study_name: studyName,
        symbol,
        timeframe,
        n_trials: studyMetadata.n_trials ?? 100,
        parameters: studyMetadata.parameters,
        created_at: studyMetadata.created_at,
        completed_at: studyMetadata.completed_at,
        sampler_seed: samplerSeed,
        min_trades_required: minTradesRequired,
      };

      return JSON.stringify(config, null, 2);
    }
  };

  const handleCopy = async (format: FormatType) => {
    const content = generateConfigContent(format);
    if (!content) return;

    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (format: FormatType) => {
    const content = generateConfigContent(format);
    if (!content) return;

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const extension = format === "toml" ? "toml" : "json";
    a.download = `${studyName}_${symbol || "study"}_${timeframe || "config"}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const isParameterPinned = (_key: string, value: unknown): boolean => {
    if (Array.isArray(value) && value.length === 2) {
      return value[0] === value[1];
    }
    return false;
  };

  const formatRangeValue = (_key: string, value: [number, number]): string => {
    const isPinned = value[0] === value[1];
    if (isPinned) {
      return `${value[0]}`;
    }
    return `[${value[0]}, ${value[1]}]`;
  };

  // Get decimal precision based on parameter type
  const getDecimalPrecision = (key: string): number => {
    // Parameters that need 2 decimal places
    if (key === 'regimethreshold') return 2;
    // Parameters that need 1 decimal place
    if (key === 'relativeweight') return 1;
    // All others are integers
    return 0;
  };

  const formatNumericValue = (key: string, value: number | number[]): string => {
    const decimals = getDecimalPrecision(key);

    // Handle array (range) values
    if (Array.isArray(value)) {
      const isPinned = value.length === 2 && value[0] === value[1];
      const formatNum = (n: number) => decimals > 0 ? n.toFixed(decimals) : Math.round(n).toString();

      if (isPinned) {
        return formatNum(value[0] ?? 0);
      }
      return `[${formatNum(value[0] ?? 0)}, ${formatNum(value[1] ?? 0)}]`;
    }

    // Handle single value
    return decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();
  };

  const getRelativeTime = (timestamp: string | null): string => {
    if (!timestamp) return "Unknown";

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (!studyMetadata.parameters) {
    return null;
  }

  // Group parameters to match ParameterConfigPanel order
  // Section 1: General Settings
  const generalSettings = [
    { key: "neighborscount_range", value: params.neighborscount_range },
    { key: "maxbarsback_range", value: params.maxbarsback_range },
    { key: "usedynamicexits", value: params.usedynamicexits },
  ];

  // Section 2: Filters Settings
  const filtersSettings = [
    { key: "useemafilter", value: params.useemafilter },
    { key: "emaperiod", value: params.emaperiod },
    { key: "usesmafilter", value: params.usesmafilter },
    { key: "smaperiod", value: params.smaperiod },
    { key: "usevolatilityfilter", value: params.usevolatilityfilter },
    { key: "useregimefilter", value: params.useregimefilter },
    { key: "regimethreshold", value: params.regimethreshold },
    { key: "useadxfilter", value: params.useadxfilter },
    { key: "adxthreshold", value: params.adxthreshold },
  ];

  // Section 3: Kernel Settings
  const kernelSettings = [
    { key: "lookbackwindow", value: params.lookbackwindow },
    { key: "relativeweight", value: params.relativeweight },
    { key: "regressionlevel", value: params.regressionlevel },
    { key: "crossoverlag", value: params.crossoverlag },
  ];

  return (
    <GlassCard className="mb-6">
      <GlassCardHeader
        className="cursor-pointer hover:bg-white/[0.03] transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <Settings className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <GlassCardTitle className="text-lg">
                Study Configuration
              </GlassCardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Input parameters and settings used for this optimization
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs"
                >
                  {copied ? (
                    <Check className="h-4 w-4 mr-1 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-1" />
                  )}
                  {copied ? "Copied!" : "Copy"}
                  <ChevronDown className="h-3 w-3 ml-1 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy("toml");
                  }}
                  className="cursor-pointer"
                >
                  <FileCode className="h-4 w-4 mr-2" />
                  Copy as TOML
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy("json");
                  }}
                  className="cursor-pointer"
                >
                  <FileJson className="h-4 w-4 mr-2" />
                  Copy as JSON
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                  <ChevronDown className="h-3 w-3 ml-1 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload("toml");
                  }}
                  className="cursor-pointer"
                >
                  <FileCode className="h-4 w-4 mr-2" />
                  Download TOML
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload("json");
                  }}
                  className="cursor-pointer"
                >
                  <FileJson className="h-4 w-4 mr-2" />
                  Download JSON
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </div>
      </GlassCardHeader>

      {isExpanded && (
        <GlassCardContent className="space-y-6">
          {/* Core Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 p-4 rounded-lg bg-white/[0.02] border border-white/5">
              <Calendar className="h-5 w-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="text-sm font-medium">
                  {getRelativeTime(studyMetadata.created_at)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {studyMetadata.created_at
                    ? new Date(studyMetadata.created_at).toLocaleDateString()
                    : "Unknown"}
                </p>
              </div>
            </div>

            {symbol && timeframe && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-white/[0.02] border border-white/5">
                <TrendingUp className="h-5 w-5 text-emerald-400 mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">Symbol & Timeframe</p>
                  <p className="text-sm font-medium">
                    {symbol} / {timeframe}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3 p-4 rounded-lg bg-white/[0.02] border border-white/5">
              <Target className="h-5 w-5 text-purple-400 mt-0.5" />
              <div>
                <p className="text-xs text-muted-foreground">Trials</p>
                <p className="text-sm font-medium">{studyMetadata.n_trials}</p>
              </div>
            </div>
          </div>

          {/* Section 1: General Settings */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <div className="h-1 w-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" />
              General Settings
            </h4>
            <div className="space-y-3">
              {generalSettings.map(({ key, value }) => {
                const displayName = getParameterDisplayName(key.replace("_range", ""));
                const description = getParameterDescription(key.replace("_range", ""));

                // Check if this is a range parameter or boolean
                const isRange = key.includes("_range");
                const isBoolean = typeof value === "boolean";

                if (isRange) {
                  const isPinned = isParameterPinned(key, value);
                  return (
                    <TooltipProvider key={key}>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors">
                            <div className="flex items-center gap-2">
                              {isPinned && (
                                <Pin className="h-3.5 w-3.5 text-amber-400" />
                              )}
                              <span className="text-sm">{displayName}</span>
                            </div>
                            <Badge
                              variant={isPinned ? "default" : "secondary"}
                              className={cn(
                                "font-mono text-xs",
                                isPinned &&
                                  "bg-amber-500/10 text-amber-400 border-amber-500/20"
                              )}
                            >
                              {formatRangeValue(key, value as [number, number])}
                            </Badge>
                          </div>
                        </TooltipTrigger>
                        {description && (
                          <TooltipContent>
                            <p className="max-w-xs">{description}</p>
                          </TooltipContent>
                        )}
                      </UITooltip>
                    </TooltipProvider>
                  );
                } else if (isBoolean) {
                  const isEnabled = Boolean(value);
                  const isPinned = pinnedParams.has(key);
                  const isOptimized = optimizedParams.has(key);

                  return (
                    <TooltipProvider key={key}>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              "flex items-center justify-between p-3 rounded-lg border transition-colors",
                              isEnabled
                                ? "bg-emerald-500/5 border-emerald-500/20 hover:bg-emerald-500/10"
                                : "bg-white/[0.02] border-white/5 hover:bg-white/[0.04]"
                            )}
                          >
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm">{displayName}</span>
                              {isPinned && (
                                <Pin className="h-3 w-3 text-blue-400 opacity-60" />
                              )}
                              {isOptimized && (
                                <Zap className="h-3 w-3 text-amber-400 opacity-70" />
                              )}
                            </div>
                            <Badge
                              variant={isEnabled ? "default" : "secondary"}
                              className={cn(
                                "text-xs",
                                isEnabled &&
                                  "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                              )}
                            >
                              {isEnabled ? "On" : "Off"}
                            </Badge>
                          </div>
                        </TooltipTrigger>
                        {description && (
                          <TooltipContent>
                            <p className="max-w-xs">
                              {description}
                              {isPinned && (
                                <span className="block mt-1 text-blue-400 text-xs">
                                  📌 Pinned (Always {isEnabled ? "ON" : "OFF"})
                                </span>
                              )}
                              {isOptimized && (
                                <span className="block mt-1 text-amber-400 text-xs">
                                  ⚡ Optimized (Tested in both On/Off states)
                                </span>
                              )}
                            </p>
                          </TooltipContent>
                        )}
                      </UITooltip>
                    </TooltipProvider>
                  );
                }
                return null;
              })}
            </div>
          </div>

          {/* Section 2: Filters Settings */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <div className="h-1 w-8 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full" />
              Filters Settings
            </h4>
            <div className="space-y-3">
              {filtersSettings.map(({ key, value }) => {
                const displayName = getParameterDisplayName(key.replace("_range", ""));
                const description = getParameterDescription(key.replace("_range", ""));

                // Check if this is a boolean or numeric parameter
                const isBoolean = typeof value === "boolean";
                const isNumeric = typeof value === "number" || Array.isArray(value);

                if (isBoolean) {
                  const isEnabled = Boolean(value);
                  const isPinned = pinnedParams.has(key);
                  const isOptimized = optimizedParams.has(key);

                  return (
                    <TooltipProvider key={key}>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              "flex items-center justify-between p-3 rounded-lg border transition-colors",
                              isEnabled
                                ? "bg-emerald-500/5 border-emerald-500/20 hover:bg-emerald-500/10"
                                : "bg-white/[0.02] border-white/5 hover:bg-white/[0.04]"
                            )}
                          >
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm">{displayName}</span>
                              {isPinned && (
                                <Pin className="h-3 w-3 text-blue-400 opacity-60" />
                              )}
                              {isOptimized && (
                                <Zap className="h-3 w-3 text-amber-400 opacity-70" />
                              )}
                            </div>
                            <Badge
                              variant={isEnabled ? "default" : "secondary"}
                              className={cn(
                                "text-xs",
                                isEnabled &&
                                  "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                              )}
                            >
                              {isEnabled ? "On" : "Off"}
                            </Badge>
                          </div>
                        </TooltipTrigger>
                        {description && (
                          <TooltipContent>
                            <p className="max-w-xs">
                              {description}
                              {isPinned && (
                                <span className="block mt-1 text-blue-400 text-xs">
                                  📌 Pinned (Always {isEnabled ? "ON" : "OFF"})
                                </span>
                              )}
                              {isOptimized && (
                                <span className="block mt-1 text-amber-400 text-xs">
                                  ⚡ Optimized (Tested in both On/Off states)
                                </span>
                              )}
                            </p>
                          </TooltipContent>
                        )}
                      </UITooltip>
                    </TooltipProvider>
                  );
                } else if (isNumeric) {
                  return (
                    <TooltipProvider key={key}>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors">
                            <span className="text-sm">{displayName}</span>
                            <span className="text-sm font-mono font-medium">
                              {formatNumericValue(key, value)}
                            </span>
                          </div>
                        </TooltipTrigger>
                        {description && (
                          <TooltipContent>
                            <p className="max-w-xs">{description}</p>
                          </TooltipContent>
                        )}
                      </UITooltip>
                    </TooltipProvider>
                  );
                }
                return null;
              })}
            </div>
          </div>

          {/* Section 3: Kernel Settings */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <div className="h-1 w-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" />
              Kernel Settings
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {kernelSettings.map(({ key, value }) => {
                const displayName = getParameterDisplayName(key);
                const description = getParameterDescription(key);

                return (
                  <TooltipProvider key={key}>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <div className="flex flex-col p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors">
                          <span className="text-xs text-muted-foreground mb-1">
                            {displayName}
                          </span>
                          <span className="text-sm font-mono font-medium">
                            {formatNumericValue(key, value)}
                          </span>
                        </div>
                      </TooltipTrigger>
                      {description && (
                        <TooltipContent>
                          <p className="max-w-xs">{description}</p>
                        </TooltipContent>
                      )}
                    </UITooltip>
                  </TooltipProvider>
                );
              })}
            </div>
          </div>
        </GlassCardContent>
      )}
    </GlassCard>
  );
}
